import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@material-ui/core'
import {ArrowBackIos, ArrowForwardIos} from '@material-ui/icons'
import React, {useEffect, useMemo, useState} from 'react'
import CloseIcon from '@material-ui/icons/Close'
import UserGuideStep0 from 'assets/img/user_guide/user_guide1.png'
import UserGuideStep1 from 'assets/img/user_guide/user_guide2.png'
import UserGuideStep2 from 'assets/img/user_guide/user_guide3.png'
import UserGuideStep3 from 'assets/img/user_guide/user_guide4.png'
import UserGuideStep4 from 'assets/img/user_guide/user_guide5.png'
import UserGuideStep5 from 'assets/img/user_guide/user_guide6.png'
import useStyles from './style'

interface UserGuideDialogProps {
  open: boolean
  onClose: () => void
}

const userGuideSteps = [
  UserGuideStep0,
  UserGuideStep1,
  UserGuideStep2,
  UserGuideStep3,
  UserGuideStep4,
  UserGuideStep5,
]

const UserGuideDialog = (props: UserGuideDialogProps) => {
  const {open, onClose} = props
  const classes = useStyles()
  const [step, setStep] = useState(0)

  const getGuideTitle = useMemo(() => {
    switch (step) {
      case 0:
      case 1:
        return 'Purchase and Recharge Method'
      case 2:
        return 'Product Description (Service Credits provided in bulk)'
      case 3:
        return 'Product Description (Service Credits provided monthly)'
      case 4:
        return 'Product Description'
      case 5:
        return 'View Usage History'
      default:
        return ''
    }
  }, [step])

  const getGuideContent = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 16,
              height: '100%',
            }}
          >
            1. Purchase Process
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - You can complete the purchase via PayPal after agreeing to the
              Terms of Use.
            </div>
          </div>
        )
      case 1:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 16,
              height: '100%',
            }}
          >
            2. Recharge Process
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - Products that have been purchased and subscribed to will display
              the label "In Operation", and a "Recharge" button will appear.
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - You can enter the desired quantity of Service Credits to
              recharge and proceed with the purchase.
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              ※ Service Credits purchased through recharging may be used until
              the product’s expiration date. Any unused Service Credits will be
              automatically forfeited.
            </div>
          </div>
        )
      case 2:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 16,
              height: '100%',
            }}
          >
            <div>
              1. The quantity of Service Credits included in the package
              described in Item 3 is provided as a one-time allocation.
            </div>
            <div>
              2. The service credits included in the package described in Item 3
              will be available for the period specified in "Duration".
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - Any unused Service Credits remaining after this period will be
              automatically forfeited. (If you run out of Service Credits, you
              may recharge them.)
            </div>
            <div>
              3. At the time of purchase, the Service Credits included in the
              package and the permitted number of uses for each service are
              displayed.
            </div>
          </div>
        )
      case 3:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 16,
              height: '100%',
            }}
          >
            <div>
              1. The quantity of Service Credits included in the package
              described in Item 3 is provided on a monthly basis.
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - Any Service Credits not used within the applicable month will be
              automatically forfeited. (If you run out of Service Credits, you
              may recharge them.)
            </div>
            <div>
              2. The package described in Item 3 provides the specified quantity
              of Service Credits once per month for a period of twelve (12)
              months.
            </div>
          </div>
        )
      case 4:
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              fontSize: 16,
              height: '100%',
            }}
          >
            <div>
              Products may be purchased multiple times and used concurrently.
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - If a service not included in the package described in Item 1 is
              included in the package described in Item 2, the corresponding
              service may be used.
            </div>
            <div
              style={{
                marginLeft: 20,
              }}
            >
              - If the same service is included in both the package described in
              Item 1 and the package described in Item 2, Service Credits will
              be deducted from the package that was purchased first.
            </div>
            <div>
              ※ EEG-related services can only be used if the basic Service
              Credit, “EEG Individual Analysis” is available. The same applies
              to HRV-related analyses.
            </div>
            <div>
              ※ The date change criteria for the product usage period is 00:00
              UTC
            </div>
          </div>
        )
      case 5:
        return (
          <div
            style={{
              fontSize: 16,
            }}
          >
            You can view usage history and the remaining number of analysis
            credits in Management {'>'} Product Usage History.
          </div>
        )
      default:
        return ''
    }
  }, [step])

  useEffect(() => {
    setStep(0)
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} className={classes.container}>
      <DialogTitle>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                backgroundColor: '#0bc7c8',
              }}
            />

            <div className={classes.guideTitle}>{getGuideTitle}</div>
          </div>
          <div className={classes.closeButtonWrap}>
            <IconButton
              color='secondary'
              aria-label='favorite'
              className={classes.closeButton}
              onClick={onClose}
            >
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent className={classes.contentWrap}>
        <div className={classes.contentContainer}>{getGuideContent}</div>
        <Divider />
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            {step > 0 && (
              <ArrowBackIos
                style={{
                  cursor: 'pointer',
                  width: 36,
                  height: 36,
                  position: 'absolute',
                  top: '50%',
                }}
                onClick={() => setStep((prevState) => prevState - 1)}
              />
            )}
          </div>
          <img
            src={userGuideSteps[step]}
            alt={`User Guide Step ${step}`}
            width={1000}
            style={{
              flex: 1,
              padding: '0 36px',
            }}
          />
          <div>
            {step < userGuideSteps.length - 1 && (
              <ArrowForwardIos
                style={{
                  cursor: 'pointer',
                  width: 36,
                  height: 36,
                  position: 'absolute',
                  top: '50%',
                  right: 0,
                }}
                onClick={() => setStep((prevState) => prevState + 1)}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UserGuideDialog
